import { useSeoMeta } from "@unhead/react";
import {
  Inbox,
  Lock,
  Mail,
  Palette,
  ShieldAlert,
  Trash2,
  Wallet,
} from "lucide-react";

import { SiteHeader } from "@/components/cashcraft/SiteHeader";
import { SiteFooter } from "@/components/cashcraft/SiteFooter";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { LoginArea } from "@/components/auth/LoginArea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useOrders, type DecryptedOrder } from "@/hooks/useOrders";
import {
  useOrderStatuses,
  type OrderStatusInfo,
} from "@/hooks/useOrderStatuses";
import { useSetOrderStatus } from "@/hooks/useSetOrderStatus";
import { useDismissedOrders } from "@/hooks/useDismissedOrders";
import { useToast } from "@/hooks/useToast";
import { cn } from "@/lib/utils";
import {
  ADMIN_PUBKEY,
  ORDER_STATUS_META,
  ORDER_STATUSES,
  PRICING_TIERS,
  SITE_TYPES,
  type OrderStatus,
} from "@/lib/cashcraft";

function labelFor(list: { value: string; label: string }[], value: string) {
  return list.find((x) => x.value === value)?.label ?? value;
}

function timeAgo(unixSeconds: number): string {
  const seconds = Math.floor(Date.now() / 1000 - unixSeconds);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  return new Date(unixSeconds * 1000).toLocaleDateString();
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const meta = ORDER_STATUS_META[status];
  return (
    <Badge variant="outline" className={cn("border", meta.className)}>
      {meta.label}
    </Badge>
  );
}

function DeleteOrderButton({ onDelete }: { onDelete: () => void }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          aria-label="Delete order"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this order?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the order from your inbox permanently. Use this to
            clear out test or duplicate orders. This can't be undone from here.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onDelete}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function OrderCard({
  order,
  statusInfo,
  onDelete,
}: {
  order: DecryptedOrder;
  statusInfo?: OrderStatusInfo;
  onDelete: () => void;
}) {
  const { event, payload, error } = order;
  const when = timeAgo(event.created_at);
  const { mutateAsync: setStatus, isPending } = useSetOrderStatus();
  const { toast } = useToast();

  const current: OrderStatus = statusInfo?.status ?? "new";

  const handleChange = async (value: string) => {
    try {
      await setStatus({ orderId: event.id, status: value as OrderStatus });
      toast({ title: `Marked as ${ORDER_STATUS_META[value as OrderStatus].label}` });
    } catch {
      toast({ title: "Couldn't update status", variant: "destructive" });
    }
  };

  if (error || !payload) {
    return (
      <Card className="border-destructive/30">
        <CardContent className="flex items-center gap-3 py-6 text-sm text-muted-foreground">
          <ShieldAlert className="h-5 w-5 shrink-0 text-destructive" />
          <span className="flex-1">
            Couldn't decrypt this order. It may not be addressed to you or is
            malformed. ({when})
          </span>
          <DeleteOrderButton onDelete={onDelete} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/60">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="flex items-start gap-1">
          <div>
            <h3 className="text-lg font-bold">{payload.project || "Untitled project"}</h3>
            <p className="text-sm text-muted-foreground">{when}</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="flex flex-wrap justify-end gap-2">
            <StatusBadge status={current} />
            <Badge variant="secondary">{labelFor(SITE_TYPES, payload.siteType)}</Badge>
            <Badge className="bg-primary text-primary-foreground">
              {labelFor(PRICING_TIERS.map((t) => ({ value: t.id, label: `${t.name} · ${t.priceUsd}` })), payload.budget)}
            </Badge>
          </div>
          <DeleteOrderButton onDelete={onDelete} />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {payload.details && (
          <p className="whitespace-pre-wrap rounded-lg bg-muted/50 p-4 text-sm">
            {payload.details}
          </p>
        )}

        {payload.features.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {payload.features.map((f) => (
              <Badge key={f} variant="outline">
                {f}
              </Badge>
            ))}
          </div>
        )}

        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="flex items-center gap-2">
            <Palette className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Style:</span>
            <span className="font-medium">{payload.style || "—"}</span>
            {payload.color && (
              <span
                className="ml-1 inline-block h-4 w-4 rounded-full border border-border"
                style={{ backgroundColor: payload.color }}
                title={payload.color}
              />
            )}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Pages:</span>
            <span className="font-medium">{payload.pages || "—"}</span>
          </div>
          {payload.contact && (
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium break-all">{payload.contact}</span>
            </div>
          )}
          {payload.bchAddress && (
            <div className="flex items-center gap-2">
              <Wallet className="h-4 w-4 text-muted-foreground" />
              <span className="font-mono text-xs break-all">{payload.bchAddress}</span>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2 border-t border-border/60 pt-4 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm font-medium text-muted-foreground">
            Update status
          </span>
          <Select
            value={current}
            onValueChange={handleChange}
            disabled={isPending}
          >
            <SelectTrigger className="w-full sm:w-56">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ORDER_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {ORDER_STATUS_META[s].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}

const Orders = () => {
  useSeoMeta({
    title: "Orders · BCH Launch",
    description: "Review incoming website build requests.",
  });

  const { user } = useCurrentUser();
  const isAdmin = user?.pubkey === ADMIN_PUBKEY;
  const { data: orders, isLoading } = useOrders();
  const { data: statuses } = useOrderStatuses();
  const { isDismissed, dismiss } = useDismissedOrders();
  const { toast } = useToast();

  const visibleOrders = (orders ?? []).filter(
    (o) => !isDismissed(o.event.id),
  );

  const handleDelete = (orderId: string) => {
    dismiss(orderId);
    toast({ title: "Order deleted" });
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <SiteHeader />
      <main className="flex-1">
        <div className="container mx-auto max-w-4xl px-4 py-12 sm:px-6">
          <div className="mb-8">
            <h1 className="text-3xl font-bold tracking-tight">Order inbox</h1>
            <p className="mt-1 text-muted-foreground">
              Private, encrypted website build requests — visible only to you.
            </p>
          </div>

          {!user && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center gap-4 py-14 text-center">
                <Lock className="h-8 w-8 text-primary" />
                <p className="max-w-sm text-muted-foreground">
                  Sign in with the BCH Launch operator account to view and
                  decrypt incoming orders.
                </p>
                <LoginArea className="max-w-xs" />
              </CardContent>
            </Card>
          )}

          {user && !isAdmin && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
                <ShieldAlert className="h-8 w-8 text-destructive" />
                <p className="max-w-sm text-muted-foreground">
                  This inbox is only accessible to the BCH Launch operator. The
                  account you're signed in with isn't authorized.
                </p>
              </CardContent>
            </Card>
          )}

          {isAdmin && isLoading && (
            <div className="space-y-4">
              {[0, 1, 2].map((i) => (
                <Card key={i}>
                  <CardHeader>
                    <Skeleton className="h-6 w-48" />
                    <Skeleton className="h-4 w-24" />
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Skeleton className="h-16 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {isAdmin && !isLoading && visibleOrders.length === 0 && (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
                <Inbox className="h-10 w-10 text-muted-foreground" />
                <p className="max-w-sm text-muted-foreground">
                  No orders yet. When customers submit a project, it'll appear
                  here — decrypted just for you.
                </p>
              </CardContent>
            </Card>
          )}

          {isAdmin && !isLoading && visibleOrders.length > 0 && (
            <div className="space-y-4">
              {visibleOrders.map((order) => (
                <OrderCard
                  key={order.event.id}
                  order={order}
                  statusInfo={statuses?.[order.event.id]}
                  onDelete={() => handleDelete(order.event.id)}
                />
              ))}
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default Orders;
